import {
	cleanPath,
	DEFAULT_DESCRIPTION,
	getBlogIndexJsonLd,
	getBlogPostJsonLd,
	getFaqJsonLd,
	getFormatPageJsonLd,
	getHomepageJsonLd,
	getSiteUrl,
} from "./metadata";
import type { PageSeoMeta } from "./types";
import { ALL_FORMAT_SLUG_EXAMPLES } from "@/data/formatterAllExamples";
import { BLOG_POSTS } from "@/data/blogPosts";

const STATIC_ROUTE_SEO: Record<string, PageSeoMeta> = {
	"/": {
		title: "Free Text Formatter | Format Text for Web, Ecommerce, and Plain Text",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/",
	},
	"/examples": {
		title: "Text Formatter Examples | Web, Ecommerce, and Plain Text",
		description:
			"Browse practical formatting examples for web content, ecommerce descriptions, and plain text cleanup.",
		canonicalPath: "/examples",
	},
	"/faq": {
		title: "FAQ | Text Formatter",
		description:
			"Find answers about Text Formatter features, free usage, API integration, and supported content types.",
		canonicalPath: "/faq",
	},
	"/pricing": {
		title: "Pricing | Text Formatter API",
		description:
			"See Text Formatter API pricing with gradual volume discounts and choose the right word volume for your usage.",
		canonicalPath: "/pricing",
	},
	"/status": {
		title: "Status | Text Formatter",
		description: "Check current Text Formatter service availability and API health.",
		canonicalPath: "/status",
	},
	"/blog": {
		title: "Blog | Text Formatter",
		description:
			"Read product updates, practical workflows, and content quality guides from the Text Formatter team.",
		canonicalPath: "/blog",
	},
	"/about": {
		title: "About | Text Formatter",
		description:
			"Learn about Text Formatter, our mission, and how we help teams publish clean, consistent content faster.",
		canonicalPath: "/about",
	},
	"/contact": {
		title: "Contact | Text Formatter",
		description: "Contact the Text Formatter team for support, product questions, and business inquiries.",
		canonicalPath: "/contact",
	},
	"/terms": {
		title: "Terms of Use | Text Formatter",
		description:
			"Read the terms of use for Text Formatter, including service use, responsibilities, and API usage conditions.",
		canonicalPath: "/terms",
	},
	"/privacy": {
		title: "Privacy Policy | Text Formatter",
		description:
			"Read how Text Formatter handles account data, API usage metadata, and privacy protection practices.",
		canonicalPath: "/privacy",
	},
	"/dashboard": {
		title: "Dashboard | Text Formatter",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/dashboard",
		noindex: true,
	},
	"/dashboard/settings": {
		title: "Settings | Text Formatter",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/dashboard/settings",
		noindex: true,
	},
	"/dashboard/admin": {
		title: "Admin Dashboard | Text Formatter",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/dashboard/admin",
		noindex: true,
	},
	"/dashboard/payment": {
		title: "Payment | Text Formatter",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/dashboard/payment",
		noindex: true,
	},
	"/login": {
		title: "LogIn | Text Formatter",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/login",
		noindex: true,
	},
	"/register": {
		title: "Register | Text Formatter",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/register",
		noindex: true,
	},
	"/forgot-password": {
		title: "Forgot Password | Text Formatter",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/forgot-password",
		noindex: true,
	},
	"/reset-password": {
		title: "Reset Password | Text Formatter",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/reset-password",
		noindex: true,
	},
	"/confirm-email": {
		title: "Confirm Email | Text Formatter",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/confirm-email",
		noindex: true,
	},
	"/resend-confirmation": {
		title: "Resend Confirmation | Text Formatter",
		description: DEFAULT_DESCRIPTION,
		canonicalPath: "/resend-confirmation",
		noindex: true,
	},
};

const NOT_FOUND_SEO: PageSeoMeta = {
	title: "Page not found | Text Formatter",
	description: "The page you requested could not be found.",
	canonicalPath: "/404",
	noindex: true,
};

const API_KEY_DETAIL_SEO: PageSeoMeta = {
	title: "API Key Detail | Text Formatter",
	description: DEFAULT_DESCRIPTION,
	canonicalPath: "/dashboard/api-key/:id",
	noindex: true,
};

const ADMIN_API_KEY_DETAIL_SEO: PageSeoMeta = {
	title: "Admin API Key Detail | Text Formatter",
	description: DEFAULT_DESCRIPTION,
	canonicalPath: "/dashboard/admin/api-key/:id",
	noindex: true,
};

export function getStaticSeoRoutes(): string[] {
	return Object.keys(STATIC_ROUTE_SEO);
}

export function resolveSeoMetaForPath(pathname: string, siteUrlInput?: string): PageSeoMeta {
	const path = cleanPath(pathname);
	const siteUrl = getSiteUrl(siteUrlInput);

	if (path.startsWith("/format/")) {
		const slug = path.replace(/^\/format\//, "");
		const example = ALL_FORMAT_SLUG_EXAMPLES.find((item) => item.slug === slug);
		const label = example?.label || slug.replace(/-/g, " ");
		const title = `${label} | Text Formatter`;
		const description =
			example?.text?.slice(0, 150).trim() || `Free Text Formatter for ${label.toLowerCase()}.`;
		const canonicalPath = `/format/${slug}`;
		return {
			title,
			description,
			canonicalPath,
			jsonLd: getFormatPageJsonLd(siteUrl, canonicalPath, title, description),
		};
	}

	if (path.startsWith("/blog/")) {
		const slug = path.replace(/^\/blog\//, "");
		const post = BLOG_POSTS.find((item) => item.slug === slug);
		if (!post) {
			return {
				title: "Page not found | Text Formatter",
				description: "The page you requested could not be found.",
				canonicalPath: `/blog/${slug}`,
				noindex: true,
			};
		}
		return {
			title: `${post.title} | Text Formatter`,
			description: post.excerpt,
			canonicalPath: `/blog/${post.slug}`,
			jsonLd: getBlogPostJsonLd(siteUrl, post),
		};
	}

	if (path.startsWith("/dashboard/api-key/")) {
		return { ...API_KEY_DETAIL_SEO, canonicalPath: path };
	}

	if (path.startsWith("/dashboard/admin/api-key/")) {
		return { ...ADMIN_API_KEY_DETAIL_SEO, canonicalPath: path };
	}

	const staticSeo = STATIC_ROUTE_SEO[path];
	if (staticSeo) {
		if (path === "/") {
			return {
				...staticSeo,
				jsonLd: getHomepageJsonLd(siteUrl),
			};
		}
		if (path === "/faq") {
			return {
				...staticSeo,
				jsonLd: getFaqJsonLd(siteUrl),
			};
		}
		if (path === "/blog") {
			return {
				...staticSeo,
				jsonLd: getBlogIndexJsonLd(siteUrl),
			};
		}
		return staticSeo;
	}

	return { ...NOT_FOUND_SEO, canonicalPath: path };
}
